import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Input,
    Select,
    Space,
    Tag,
    Typography,
    Modal,
    Form,
    message,
    Dropdown,
    Alert,
    Row,
    Col,
    Badge,
    Checkbox,
} from 'antd';
import {
    UserOutlined,
    EditOutlined,
    MoreOutlined,
    ExportOutlined,
    ReloadOutlined,
    PlusOutlined,
    SearchOutlined,
    MailOutlined,
    LockOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined, TeamOutlined,
} from '@ant-design/icons';
import {
    useGetAdminUsersQuery,
    useCreateAdminUserMutation,
    useUpdateAdminUserMutation,
    useResendAdminUserInviteMutation,
    useResetAdminUserPasswordMutation,
    useGetAdminOrganizationsQuery,
    useGetAdminAnalyticsQuery,
} from '../../services/nestjs-base-api';
import { IApiUser } from '../../services/nestjs-base-api';
import { GeneralStatusEnum } from '../../enums/GeneralStatusEnum';
import { useAuth } from '../../hooks/useAuth';
import { Utils } from '../../utils/utils';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ResponsiveTable } from '../components/ResponsiveTable';
import { StatisticWithMock } from '../components/StatisticWithMock';

// Extended interface for admin users with additional fields
interface IAdminUser extends IApiUser {
    lastLoginAt?: string;
    hash?: string;
    organisationId?: number;
    organisationName?: string;
    isFromMockOrg?: boolean; // Flag to identify users from mock organizations
}

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;

export const AdminUsersPage: React.FC = () => {
    const { user } = useAuth();
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [roleFilter, setRoleFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [organizationFilter, setOrganizationFilter] = useState<string>('all');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState<IAdminUser | null>(null);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();

    const isSuperAdmin = Utils.userIsSuperAdmin(user);

    const { data: usersData, isLoading, error, refetch } = useGetAdminUsersQuery({
        jwtToken: user?.jwtToken || '',
        page: currentPage,
        limit: pageSize,
        search: searchText || undefined,
    });

    const { data: organizationsData } = useGetAdminOrganizationsQuery({
        jwtToken: user?.jwtToken || '',
        limit: 100,
        status: 'ACTIVE',
    });

    const { data: analytics } = useGetAdminAnalyticsQuery({
        jwtToken: user?.jwtToken || '',
    });

    const [createUser] = useCreateAdminUserMutation();
    const [updateUser] = useUpdateAdminUserMutation();
    const [resendInvite] = useResendAdminUserInviteMutation();
    const [resetPassword] = useResetAdminUserPasswordMutation();

    const users = (usersData?.data || []) as IAdminUser[];
    const totalUsers = usersData?.total || 0;

    // CSS for mock data styling
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .mock-user-row {
                background-color: #fff7e6 !important;
                border-left: 4px solid #fa8c16 !important;
            }
            .mock-user-row:hover {
                background-color: #ffe7ba !important;
            }
            .mock-user-row td {
                background-color: inherit !important;
            }
        `;
        document.head.appendChild(style);
        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);

    // Filter users based on selected filters
    const filteredUsers = users.filter(user => {
        const rolesArray = typeof user.roles === 'string' ? JSON.parse(user.roles) : user.roles;
        const matchesRole = roleFilter === 'all' || rolesArray.includes(roleFilter);
        const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
        const matchesOrganization = organizationFilter === 'all' || 
            (organizationFilter === 'none' && !user.organisationName) ||
            (user.organisationId && user.organisationId.toString() === organizationFilter);
        return matchesRole && matchesStatus && matchesOrganization;
    });

    const getRoleTag = (roles: string[]) => {
        const roleColors: { [key: string]: string } = {
            'SUPER_ADMIN': 'purple',
            'ADMIN': 'blue',
            'COMPANY_ADMIN': 'green',
            'COMPANY_VIEWER': 'default',
        };

        const roleLabels: { [key: string]: string } = {
            'SUPER_ADMIN': 'Super Admin',
            'ADMIN': 'Admin',
            'COMPANY_ADMIN': 'Organisation Admin',
            'COMPANY_VIEWER': 'Organisation Viewer',
        };

        return roles.map(role => (
            <Tag key={role} color={roleColors[role] || 'default'}>
                {roleLabels[role] || role}
            </Tag>
        ));
    };

    const getStatusBadge = (status: GeneralStatusEnum) => {
        const statusConfig: Record<GeneralStatusEnum, { color: 'success' | 'warning' | 'error' | 'default', icon: React.ReactNode }> = {
            [GeneralStatusEnum.Active]: { color: 'success', icon: <CheckCircleOutlined /> },
            [GeneralStatusEnum.Suspended]: { color: 'error', icon: <CloseCircleOutlined /> },
            [GeneralStatusEnum.Deleted]: { color: 'warning', icon: <CloseCircleOutlined /> },
        };

        const config = statusConfig[status] || { color: 'default', icon: null };

        return (
            <Badge status={config.color as any} text={
                <Space>
                    {config.icon}
                    {status}
                </Space>
            } />
        );
    };

    const handleCreateUser = async (values: any) => {
        try {
            const payload = {
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                roles: [values.role], // Convert single role to array
                ...(values.organisationId && { organisationId: values.organisationId }),
                ...(values.suppressInviteEmail && { suppressInviteEmail: true }),
            };

            await createUser({
                jwtToken: user?.jwtToken || '',
                payload,
            }).unwrap();

            message.success(values.suppressInviteEmail
                ? 'User created successfully. No invitation email was sent — use Resend Invite when ready.'
                : 'User created successfully. An invitation email has been sent.');
            setIsCreateModalVisible(false);
            form.resetFields();
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to create user');
        }
    };

    const handleEditUser = (user: IAdminUser) => {
        setSelectedUser(user);
        const userRoles = typeof user.roles === 'string' ? JSON.parse(user.roles) : user.roles;
        editForm.setFieldsValue({
            firstName: user.firstName,
            lastName: user.lastName,
            status: user.status,
            roles: Array.isArray(userRoles) ? userRoles[0] : userRoles,
            organisationId: user.organisationId,
        });
        setIsEditModalVisible(true);
    };

    const handleUpdateUser = async (values: any) => {
        try {
            if (!selectedUser) return;

            const payload = {
                firstName: values.firstName,
                lastName: values.lastName,
                status: values.status,
                roles: [values.roles], // Convert single role to array
                ...(values.organisationId && { organisationId: values.organisationId }),
            };

            await updateUser({
                jwtToken: user?.jwtToken || '',
                userId: selectedUser.id,
                payload,
            }).unwrap();

            message.success('User updated successfully');
            setIsEditModalVisible(false);
            editForm.resetFields();
            setSelectedUser(null);
            refetch();
        } catch (error) {
            message.error('Failed to update user');
        }
    };

    const handleResendInvite = async (userId: number) => {
        try {
            await resendInvite({
                jwtToken: user?.jwtToken || '',
                userId: userId,
            }).unwrap();
            message.success('Invitation email resent successfully');
            refetch();
        } catch (error) {
            message.error('Failed to resend invitation');
        }
    };

    const getActionItems = (record: IAdminUser) => {
        const items = [
            {
                key: 'edit',
                label: 'Edit User',
                icon: <EditOutlined />,
                onClick: () => handleEditUser(record),
            },
            {
                key: 'resend',
                label: 'Resend Invite',
                icon: <MailOutlined />,
                onClick: () => handleResendInvite(record.id),
            },
        ];
        return items;
    };

    const handleResetPassword = async (userId: number) => {
        try {
            await resetPassword({
                jwtToken: user?.jwtToken || '',
                userId: userId,
            }).unwrap();
            message.success('Password reset email sent successfully');
            refetch();
        } catch (error) {
            message.error('Failed to send password reset email');
        }
    };

    const columns: ColumnsType<IAdminUser> = [
        {
            title: 'User',
            key: 'user',
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text strong>{`${record.firstName || ''} ${record.lastName || ''}`}</Text>
                        {record.isFromMockOrg && (
                            <Tag color="orange" style={{fontSize: '10px'}}>
                                MOCK ORG
                            </Tag>
                        )}
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>{record.email}</Text>
                </Space>
            ),
        },
        {
            title: 'Role',
            dataIndex: 'roles',
            key: 'roles',
            render: (roles: string | string[]) => {
                const rolesArray = typeof roles === 'string' ? JSON.parse(roles) : roles;
                return getRoleTag(rolesArray);
            },
            filters: [
                { text: 'Super Admin', value: 'SUPER_ADMIN' },
                { text: 'Admin', value: 'ADMIN' },
                { text: 'Organisation Admin', value: 'COMPANY_ADMIN' },
                { text: 'Organisation Viewer', value: 'COMPANY_VIEWER' },
            ],
            onFilter: (value, record) => {
                const rolesArray = typeof record.roles === 'string' ? JSON.parse(record.roles) : record.roles;
                return rolesArray.includes(value as string);
            },
        },
        {
            title: 'Organisation',
            key: 'organization',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{record.organisationName || '-'}</span>
                    {record.isFromMockOrg && record.organisationName && (
                        <Tag color="orange" style={{fontSize: '9px'}}>
                            MOCK
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: GeneralStatusEnum) => getStatusBadge(status),
            filters: [
                { text: 'Active', value: GeneralStatusEnum.Active },
                { text: 'Suspended', value: GeneralStatusEnum.Suspended },
                { text: 'Deleted', value: GeneralStatusEnum.Deleted },
            ],
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Last Login',
            dataIndex: 'lastLoginAt',
            key: 'lastLoginAt',
            render: (date: string) => date ? dayjs(date).fromNow() : 'Never',
            sorter: (a, b) => dayjs(a.lastLoginAt || 0).unix() - dayjs(b.lastLoginAt || 0).unix(),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
            sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const items = [
                    {
                        key: 'edit',
                        label: 'Edit User',
                        icon: <EditOutlined />,
                        onClick: () => handleEditUser(record),
                    },
                    {
                        key: 'resend',
                        label: 'Resend Invite',
                        icon: <MailOutlined />,
                        onClick: () => handleResendInvite(record.id),
                    },
                    {
                        key: 'reset',
                        label: 'Reset Password',
                        icon: <LockOutlined />,
                        onClick: () => handleResetPassword(record.id),
                    },
                    {
                        key: 'divider1',
                        type: 'divider' as const,
                    },
                    {
                        key: 'suspend',
                        label: record.status === GeneralStatusEnum.Active ? 'Suspend User' : 'Activate User',
                        danger: record.status === GeneralStatusEnum.Active,
                        onClick: () => {
                            // TODO: Implement suspend/activate user
                        },
                    },
                ];

                return (
                    <Dropdown menu={{ items }} trigger={['click']}>
                        <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                );
            },
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
    };

    // Separate real and mock users from current page
    const pageRealUsers = users.filter(u => !u.isFromMockOrg);
    const pageMockUsers = users.filter(u => u.isFromMockOrg);

    // Use platform analytics for totals when not filtered, otherwise use page counts
    const isFiltered = searchText || statusFilter !== 'all' || roleFilter !== 'all' || organizationFilter !== 'all';

    const summary = {
        // If showing all users (no filters), use platform totals, otherwise use filtered totals
        total: !isFiltered && analytics ? analytics.totalUsers : totalUsers,
        totalReal: !isFiltered && analytics ? (analytics.totalUsers - (analytics.mockUsers || 0)) : totalUsers - pageMockUsers.length,
        totalMock: !isFiltered && analytics ? (analytics.mockUsers || 0) : pageMockUsers.length,
        // For status counts, we'll still need to show page-specific counts since we don't have platform-wide status breakdowns
        // but for the total card, we should show the platform total when not filtered
        active: users.filter(u => u.status === GeneralStatusEnum.Active).length,
        activeReal: pageRealUsers.filter(u => u.status === GeneralStatusEnum.Active).length,
        activeMock: pageMockUsers.filter(u => u.status === GeneralStatusEnum.Active).length,
        suspended: users.filter(u => u.status === GeneralStatusEnum.Suspended).length,
        suspendedReal: pageRealUsers.filter(u => u.status === GeneralStatusEnum.Suspended).length,
        suspendedMock: pageMockUsers.filter(u => u.status === GeneralStatusEnum.Suspended).length,
        deleted: users.filter(u => u.status === GeneralStatusEnum.Deleted).length,
        deletedReal: pageRealUsers.filter(u => u.status === GeneralStatusEnum.Deleted).length,
        deletedMock: pageMockUsers.filter(u => u.status === GeneralStatusEnum.Deleted).length,
        showMockData: !isFiltered ? (analytics?.showMockData || false) : pageMockUsers.length > 0,
    };

    const availableRoles = isSuperAdmin
        ? [
            { value: 'SUPER_ADMIN', label: 'Super Admin' },
            { value: 'ADMIN', label: 'Admin' },
            { value: 'COMPANY_ADMIN', label: 'Organisation Admin' },
            { value: 'COMPANY_VIEWER', label: 'Organisation Viewer' },
        ]
        : [
            { value: 'ADMIN', label: 'Admin' },
            { value: 'COMPANY_ADMIN', label: 'Organisation Admin' },
            { value: 'COMPANY_VIEWER', label: 'Organisation Viewer' },
        ];

    const requiresOrganisation = (role: string) => {
        return role === 'COMPANY_ADMIN' || role === 'COMPANY_VIEWER';
    };

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}>
                    <TeamOutlined style={{ marginRight: '8px' }}/>
                    Users
                </Title>
                <Space>
                    <Button icon={<ExportOutlined />}>Export</Button>
                    <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setIsCreateModalVisible(true)}
                    >
                        Create User
                    </Button>
                </Space>
            </div>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={6}>
                    <Card>
                        <StatisticWithMock
                            title="Total Users"
                            realValue={summary.totalReal}
                            mockValue={summary.totalMock}
                            prefix={<UserOutlined />}
                            showMockData={summary.showMockData}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <StatisticWithMock
                            title="Active"
                            realValue={summary.activeReal}
                            mockValue={summary.activeMock}
                            showMockData={summary.showMockData}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <StatisticWithMock
                            title="Suspended"
                            realValue={summary.suspendedReal}
                            mockValue={summary.suspendedMock}
                            showMockData={summary.showMockData}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <StatisticWithMock
                            title="Deleted"
                            realValue={summary.deletedReal}
                            mockValue={summary.deletedMock}
                            showMockData={summary.showMockData}
                        />
                    </Card>
                </Col>
            </Row>

            <Card>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder="Search by name or email..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="All Organisations"
                            value={organizationFilter}
                            onChange={setOrganizationFilter}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">All Organisations</Option>
                            <Option value="none">No Organisation</Option>
                            {organizationsData?.data.map(org => (
                                <Option key={org.id} value={org.id.toString()}>
                                    {org.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="All Roles"
                            value={roleFilter}
                            onChange={setRoleFilter}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">All Roles</Option>
                            <Option value="SUPER_ADMIN">Super Admin</Option>
                            <Option value="ADMIN">Admin</Option>
                            <Option value="COMPANY_ADMIN">Organisation Admin</Option>
                            <Option value="COMPANY_VIEWER">Organisation Viewer</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="All Status"
                            value={statusFilter}
                            onChange={setStatusFilter}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">All Status</Option>
                            <Option value={GeneralStatusEnum.Active}>Active</Option>
                            <Option value={GeneralStatusEnum.Suspended}>Suspended</Option>
                            <Option value={GeneralStatusEnum.Deleted}>Deleted</Option>
                        </Select>
                    </Col>
                </Row>

                {selectedRowKeys.length > 0 && (
                    <Alert
                        message={
                            <Space>
                                <Text>{selectedRowKeys.length} users selected</Text>
                                <Button size="small" type="link">Bulk Edit</Button>
                                <Button size="small" type="link" danger>Bulk Suspend</Button>
                            </Space>
                        }
                        type="info"
                        showIcon
                        closable
                        onClose={() => setSelectedRowKeys([])}
                        style={{ marginBottom: 16 }}
                    />
                )}

                <ResponsiveTable
                    rowSelection={rowSelection}
                    columns={columns}
                    rowClassName={(record: IAdminUser) => record.isFromMockOrg ? 'mock-user-row' : ''}
                    mobileColumns={[
                        {
                            title: 'User',
                            key: 'user',
                            render: (_, record) => (
                                <Space direction="vertical" size="small">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <strong>{record.firstName} {record.lastName}</strong>
                                        {record.isFromMockOrg && (
                                            <Tag color="orange" style={{fontSize: '10px'}}>
                                                MOCK ORG
                                            </Tag>
                                        )}
                                    </div>
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        {record.email}
                                    </Typography.Text>
                                    <Space size="small">
                                        <Badge 
                                            status={record.status === GeneralStatusEnum.Active ? 'success' : 'error'}
                                            text={record.status}
                                        />
                                        {record.organisationName && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <Tag>{record.organisationName}</Tag>
                                                {record.isFromMockOrg && (
                                                    <Tag color="orange" style={{fontSize: '9px'}}>
                                                        MOCK
                                                    </Tag>
                                                )}
                                            </div>
                                        )}
                                    </Space>
                                </Space>
                            ),
                        },
                        {
                            title: 'Actions',
                            key: 'actions',
                            width: 100,
                            render: (_, record) => (
                                <Dropdown
                                    menu={{
                                        items: getActionItems(record)
                                    }}
                                    trigger={['click']}
                                >
                                    <Button icon={<MoreOutlined />} />
                                </Dropdown>
                            ),
                        },
                    ]}
                    dataSource={filteredUsers}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalUsers,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size || 10);
                        },
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
                    }}
                />
            </Card>

            {/* Create User Modal */}
            <Modal
                title="Create New User"
                open={isCreateModalVisible}
                onCancel={() => {
                    setIsCreateModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateUser}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="First Name"
                                name="firstName"
                                rules={[{ required: true, message: 'Please enter first name' }]}
                            >
                                <Input placeholder="John" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Last Name"
                                name="lastName"
                                rules={[{ required: true, message: 'Please enter last name' }]}
                            >
                                <Input placeholder="Doe" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Email Address"
                        name="email"
                        rules={[
                            { required: true, message: 'Please enter email' },
                            { type: 'email', message: 'Please enter a valid email' },
                        ]}
                    >
                        <Input placeholder="john.doe@example.com" />
                    </Form.Item>

                    <Form.Item
                        label="Role"
                        name="role"
                        rules={[{ required: true, message: 'Please select a role' }]}
                    >
                        <Select placeholder="Select a role" onChange={(value) => {
                            if (!requiresOrganisation(value)) {
                                form.setFieldsValue({ organisationId: undefined });
                            }
                        }}>
                            {availableRoles.map(role => (
                                <Option key={role.value} value={role.value}>
                                    {role.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}>
                        {({ getFieldValue }) =>
                            requiresOrganisation(getFieldValue('role')) ? (
                                <Form.Item
                                    label="Organisation"
                                    name="organisationId"
                                    rules={[{ required: true, message: 'Please select an organisation' }]}
                                >
                                    <Select placeholder="Select an organisation">
                                        {organizationsData?.data.map(org => (
                                            <Option key={org.id} value={org.id}>
                                                {org.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item name="suppressInviteEmail" valuePropName="checked" style={{ marginBottom: 8 }}>
                        <Checkbox>Don't send the invitation email yet (send it later via Resend Invite)</Checkbox>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.suppressInviteEmail !== currentValues.suppressInviteEmail}>
                        {({ getFieldValue }) =>
                            getFieldValue('suppressInviteEmail') ? (
                                <Alert
                                    message="No email will be sent. The user stays suspended until you use Resend Invite to send their password-set link."
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />
                            ) : (
                                <Alert
                                    message="The user will receive an email invitation to set their password and access the platform."
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />
                            )
                        }
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                Create User
                            </Button>
                            <Button onClick={() => {
                                setIsCreateModalVisible(false);
                                form.resetFields();
                            }}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit User Modal */}
            <Modal
                title="Edit User"
                open={isEditModalVisible}
                onCancel={() => {
                    setIsEditModalVisible(false);
                    editForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={handleUpdateUser}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="First Name"
                                name="firstName"
                                rules={[{ required: true, message: 'Please enter first name' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Last Name"
                                name="lastName"
                                rules={[{ required: true, message: 'Please enter last name' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Status"
                        name="status"
                        rules={[{ required: true, message: 'Please select status' }]}
                    >
                        <Select>
                            <Option value={GeneralStatusEnum.Active}>Active</Option>
                            <Option value={GeneralStatusEnum.Suspended}>Suspended</Option>
                            <Option value={GeneralStatusEnum.Deleted}>Deleted</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Role"
                        name="roles"
                        rules={[{ required: true, message: 'Please select a role' }]}
                    >
                        <Select placeholder="Select a role">
                            {availableRoles.map(role => (
                                <Option key={role.value} value={role.value}>
                                    {role.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.roles !== currentValues.roles}>
                        {({ getFieldValue }) =>
                            requiresOrganisation(getFieldValue('roles')) ? (
                                <Form.Item
                                    label="Organisation"
                                    name="organisationId"
                                >
                                    <Select placeholder="Select an organisation">
                                        {organizationsData?.data.map(org => (
                                            <Option key={org.id} value={org.id}>
                                                {org.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                Update User
                            </Button>
                            <Button onClick={() => {
                                setIsEditModalVisible(false);
                                editForm.resetFields();
                            }}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};